// HelpShotGallery — 사용 안내·관리자 사용 안내가 함께 쓰는 설명서 캡처 갤러리.
//
// 왜 있는가: 글만 있는 안내는 "그 화면이 어디냐"를 못 알려준다. 캡처를 붙이되
//  ① 첫 화면이 이미지 때문에 멈추지 않게 loading="lazy"
//  ② 작은 캡처는 글씨가 안 보이니 눌러서 크게 보기(Esc·✕·덮개 클릭으로 닫힘)
//  ③ 휴대폰으로 보는 사람에게는 휴대폰 캡처를 먼저
// 를 한 곳에서 처리한다.
//
// 🔴 이름→캡처 표는 `shots` 인자로 받는다 — 사용 안내는 SHOTS(utils/helpShots),
//    관리자 탭은 ADMIN_SHOTS(utils/adminHelpShots)를 넘긴다. 관리자 캡처를 여기서
//    import 하면 로그인 화면이 받는 메인 묶음에 관리자 화면 캡처가 섞인다.
// 🔴 표에 없는 이름은 조용히 건너뛴다(화면이 깨지지 않게) — 빠짐은 전수 시험이 막는다.
import { useEffect, useState } from 'react';
import { SHOTS } from '../utils/helpShots';
import { COLORS, btn, RADIUS } from '../utils/uiStyles';

const HINT = '화면 예시 — 누르면 크게 볼 수 있어요.';
// 사진 상자 그림자는 SHADOW 토큰 둘(btn·float) 어느 쪽과도 값이 달라 그대로 둔다
const FIGURE = {
  margin: 0, background: COLORS.surface, border: `1px solid ${COLORS.borderSoft}`, borderRadius: RADIUS.lg,
  boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: '6px', boxSizing: 'border-box',
};
const CAPTION = { fontSize: '12px', color: COLORS.textSub, textAlign: 'center', marginTop: '6px', wordBreak: 'keep-all' };

function Shot({ shot, phone, onOpen }) {
  return (
    <figure style={{ ...FIGURE, ...(phone ? { flex: '0 0 auto', width: '260px', maxWidth: '100%' } : { maxWidth: '100%' }) }}>
      <button type="button" onClick={() => onOpen(shot)} aria-label={`${shot.caption} 크게 보기`}
        style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'none', cursor: 'zoom-in' }}>
        <img src={shot.src} alt={shot.caption} loading="lazy" decoding="async"
          style={{ display: 'block', width: '100%', maxWidth: '100%', height: 'auto', borderRadius: '6px', cursor: 'zoom-in' }} />
      </button>
      <figcaption style={CAPTION}>{shot.caption}</figcaption>
    </figure>
  );
}

// 크게 보기. 확대된 이미지 자체를 눌러서는 닫히지 않는다(읽는 중에 닫히면 성가시다).
function ShotZoom({ shot, onClose }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={shot.caption}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,.82)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '24px' }}>
      <button type="button" onClick={onClose} aria-label="닫기"
        style={{ position: 'absolute', top: '14px', right: '16px', width: '34px', height: '34px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,.92)', color: '#1f2937', fontSize: '17px', cursor: 'pointer' }}>✕</button>
      <img src={shot.src} alt={shot.caption}
        style={{ maxWidth: '95vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: '8px', background: COLORS.surface }} />
      <div style={{ fontSize: '13px', color: 'white', textAlign: 'center' }}>{shot.caption}</div>
    </div>
  );
}

export default function HelpShotGallery({ names = [], phoneNames = [], mobile = false, compact = false, shots = SHOTS }) {
  const [open, setOpen] = useState(!compact);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setZoom(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoom]);

  const pick = (list) => (list || []).reduce((acc, name) => {
    const shot = shots && shots[name];
    if (shot && shot.src) acc.push({ name, ...shot });
    return acc;
  }, []);
  const pc = pick(names);
  const phone = pick(phoneNames);
  if (!pc.length && !phone.length) return null;

  if (compact && !open) {
    return (
      <button type="button" className="gw-btn gw-btn--secondary" onClick={() => setOpen(true)}
        style={btn('secondary')}>
        {`🖼 화면 보기 (${pc.length + phone.length})`}
      </button>
    );
  }

  const pcGroup = pc.length ? (
    <div key="pc" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {pc.map((shot) => <Shot key={shot.name} shot={shot} onOpen={setZoom} />)}
    </div>
  ) : null;
  const phoneGroup = phone.length ? (
    <div key="phone">
      {!mobile && <div style={{ fontSize: '12px', fontWeight: 600, color: COLORS.textSub, marginBottom: '8px' }}>휴대폰 화면</div>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', justifyContent: 'center' }}>
        {phone.map((shot) => <Shot key={shot.name} shot={shot} phone onOpen={setZoom} />)}
      </div>
    </div>
  ) : null;

  return (
    <div>
      <div style={{ fontSize: '12px', color: COLORS.textSub, marginBottom: '8px' }}>{HINT}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {mobile ? [phoneGroup, pcGroup] : [pcGroup, phoneGroup]}
      </div>
      {zoom && <ShotZoom shot={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}
